import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Button } from '@televet/kibble-ui/Button';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { trpc } from '@/lib/trpc';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { DeleteDialog } from '@/components/DeleteDialog';
import { RowActionButton } from '@/components/RowActionButton';
import { toaster } from '@/components/ui/toaster';
import { FilterMenu } from '@/features/listing-filters/FilterMenu';
import { FilterPills, type FilterPill } from '@/features/listing-filters/FilterPills';
import { buildGroupPills } from '@/features/listing-filters/groupPills';
import type { FilterGroup, FilterSelection } from '@/features/listing-filters/types';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  CONNECTIONS,
  QUERY_TYPES,
  templateFiltersSchema,
  type TemplateFilterValues,
} from './-lib/filters';
import { LastEdited } from './-components/LastEdited';

const FILTER_GROUPS: FilterGroup[] = [
  {
    key: 'queryType',
    label: 'Type',
    options: QUERY_TYPES.map((type) => ({ label: type, value: type })),
  },
  {
    key: 'connection',
    label: 'Connection',
    options: CONNECTIONS.map((connection) => ({ label: connection, value: connection })),
  },
  {
    key: 'published',
    label: 'Status',
    options: [
      { label: 'Published', value: 'published' },
      { label: 'Draft', value: 'draft' },
    ],
  },
];

export const Route = createFileRoute('/_authenticated/emitter-templates/')({
  component: EmitterTemplates,
  validateSearch: templateFiltersSchema,
});

function EmitterTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routeNavigate = Route.useNavigate();
  const filters = Route.useSearch();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [nameDraft, setNameDraft] = useDebouncedFilterText({
    value: filters.name,
    onCommit: (next) =>
      routeNavigate({ replace: true, search: (prev) => ({ ...prev, name: next }) }),
  });

  const applyFilters = useCallback(
    (next: TemplateFilterValues) => {
      routeNavigate({ search: next });
    },
    [routeNavigate]
  );

  useListingFilters({
    page: 'emitter-templates',
    schema: templateFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const templatesQuery = useQuery(
    trpc.emitter.listTemplates.queryOptions(
      useMemo(
        () => ({
          ...(filters.name && { name: filters.name }),
          ...(filters.queryType?.length && { queryType: filters.queryType }),
          ...(filters.connection?.length && { connection: filters.connection }),
          ...(filters.published && { isPublished: filters.published === 'published' }),
        }),
        [filters]
      ),
      { placeholderData: keepPreviousData }
    )
  );
  const templates = templatesQuery.data;

  const selection = useMemo<FilterSelection>(
    () => ({
      queryType: filters.queryType ?? [],
      connection: filters.connection ?? [],
      published: filters.published ? [filters.published] : [],
    }),
    [filters]
  );

  const handleSelectionChange = useCallback(
    (next: FilterSelection) => {
      applyFilters({
        ...filters,
        queryType: next.queryType as TemplateFilterValues['queryType'],
        connection: next.connection as TemplateFilterValues['connection'],
        // Published and Draft are mutually exclusive, so the most recently
        // added choice wins rather than both applying and matching nothing.
        published: (next.published.find((value) => value !== filters.published) ??
          next.published[0]) as TemplateFilterValues['published'],
      });
    },
    [applyFilters, filters]
  );

  const pills = useMemo<FilterPill[]>(() => {
    const all = buildGroupPills({
      groups: FILTER_GROUPS,
      selection,
      onChange: handleSelectionChange,
    });

    if (filters.name) {
      all.push({
        key: 'name',
        label: `Name: ${filters.name}`,
        onRemove: () => applyFilters({ ...filters, name: undefined }),
      });
    }
    return all;
  }, [selection, handleSelectionChange, filters, applyFilters]);

  const { mutate: deleteTemplate, isPending: isDeleting } = useMutation(
    trpc.emitter.deleteTemplate.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Template deleted' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listTemplates']],
        });
        setDeleteId(null);
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to delete',
          description: error.message,
        });
      },
    })
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs items={[{ label: 'Templates' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Templates
          </Text>
          <Button
            size="sm"
            iconName="plus"
            onClick={() => navigate({ to: '/emitter-templates/add' })}
          >
            New Template
          </Button>
        </Flex>

        <Flex direction="column" gap={3}>
          <Flex gap={3} align="flex-end" wrap="wrap">
            <TextInput
              label="Name"
              // The width has to go on the form control wrapper: `maxW` lands on
              // the inner input and leaves the wrapper full width, which pushes
              // everything after it onto its own row.
              formControlStyle={{ width: '240px', flexShrink: 0 }}
              placeholder="Search templates..."
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              size="sm"
              data-testid="templates-filter-name"
            />
            <FilterMenu
              groups={FILTER_GROUPS}
              selection={selection}
              onChange={handleSelectionChange}
            />
          </Flex>
          <FilterPills
            pills={pills}
            onClearAll={() => {
              setNameDraft('');
              applyFilters({
                name: undefined,
                queryType: [],
                connection: [],
                published: undefined,
              });
            }}
          />
        </Flex>

        <Table.Root size="sm" width="100%" variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Connection</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Version</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Status</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Instances</Table.ColumnHeader>
              <Table.ColumnHeader>Updated</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {templates?.map((template) => (
              <Table.Row
                key={template.id}
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
                onClick={() =>
                  navigate({
                    to: `/emitter-templates/${template.id}`,
                  })
                }
              >
                <Table.Cell>
                  <Text fontSize="sm" fontWeight="medium">
                    {template.name}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Tag label={template.queryType} size="sm" />
                </Table.Cell>
                <Table.Cell>
                  {/* Same badge as the Type column beside it. */}
                  <Tag label={template.connection} size="sm" />
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Text fontSize="xs" variant="subtle">
                    v{template.currentVersion}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Tag
                    label={template.isPublished ? 'Published' : 'Draft'}
                    variant={template.isPublished ? 'success' : 'default'}
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Text fontSize="xs" variant="subtle">
                    {template._count.emitters}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <LastEdited at={template.lastEditedAt} by={template.lastEditedByUser} />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <Flex gap={0.5} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                    <RowActionButton
                      label="View instances"
                      iconName="list"
                      onClick={() =>
                        navigate({
                          to: `/emitter-templates/${template.id}/instances`,
                        })
                      }
                      data-testid={`template-instances-${template.id}`}
                    />
                    <RowActionButton
                      label="Edit template"
                      iconName="pen"
                      onClick={() =>
                        navigate({
                          to: `/emitter-templates/${template.id}`,
                        })
                      }
                      data-testid={`template-edit-${template.id}`}
                    />
                    <RowActionButton
                      label="Delete template"
                      iconName="trashcan"
                      variant="ghostDestructive"
                      onClick={() => setDeleteId(template.id)}
                      data-testid={`template-delete-${template.id}`}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        {!templatesQuery.isLoading && templates?.length === 0 && (
          <Flex justify="center" py={6}>
            <Text fontSize="sm" variant="subtle">
              {pills.length > 0 ? 'No templates match these filters' : 'No templates yet'}
            </Text>
          </Flex>
        )}
      </Flex>

      <DeleteDialog
        isOpen={!!deleteId}
        title="Delete Template"
        description="Are you sure you want to delete this template? This action cannot be undone."
        onConfirm={() => deleteId && deleteTemplate({ id: deleteId })}
        onCancel={() => setDeleteId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
